<template>
  <v-container id="order-details" fluid v-if="subOrder">
    <v-card outlined>
      <v-container fluid>
        <order-headers-mobile :order="subOrder" :is-sub-order="true" v-if="isMobile" />
        <order-headers :order="subOrder" :is-sub-order="true" v-else />
        <v-toolbar
          v-if="
            isScanStep || !isMobile || !['cancelled', 'waiting', 'waiting_allocation'].includes(subOrder.current_status)
          "
          flat
          color="white"
          class="pl-0"
        >
          <span v-if="mustDisplayAnyLocker">
            <v-card-text v-if="isMobile" class="pr-0 pl-1">
              <span v-if="subOrder.reception_locker_id">
                <span v-if="subOrder.reception_locker_id !== 'no_available_locker'">
                  {{ $t('subOrder.toPickupInLocker') }} {{ subOrder.reception_locker_id }}
                </span>
              </span>
              <span v-else-if="mustDisplayPreparationLocker">
                {{ $t('subOrder.toPickupInLocker') + ' ' + subOrder.preparation_locker_id }}
              </span>
            </v-card-text>
            <v-card-title v-else class="text--primary pl-0">
              <span v-if="subOrder.reception_locker_id">
                <span v-if="subOrder.reception_locker_id !== 'no_available_locker'">
                  {{ $t('subOrder.toPickupInLocker') }} {{ subOrder.reception_locker_id }}
                </span>
              </span>
              <span v-else-if="mustDisplayPreparationLocker">
                {{ $t('subOrder.toPickupInLocker') + ' ' + subOrder.preparation_locker_id }}
              </span>
            </v-card-title>
          </span>
          <v-spacer></v-spacer>
          <v-btn
            v-if="isScanStep && checkItemForEan()"
            :disabled="allProductAreScanned"
            outlined
            fab
            small
            right
            @click="openBarcodeDialog"
            class="mr-1"
          >
            <v-icon>mdi-barcode</v-icon>
          </v-btn>
          <v-btn v-if="!isMobile" outlined fab small right @click="listDisplay = !listDisplay">
            <v-icon v-if="listDisplay">mdi-apps</v-icon>
            <v-icon v-else>mdi-format-list-bulleted-square</v-icon>
          </v-btn>
          <v-btn
            class="mx-2"
            outlined
            fab
            small
            right
            v-if="!['cancelled', 'waiting', 'waiting_allocation'].includes(subOrder.current_status)"
            @click="downloadPDF()"
            :loading="pdfLoading"
          >
            <v-icon>mdi-file-pdf-box</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <!-- Stepper header - only when box selection is active in scan step -->
        <v-stepper
          v-if="isScanStep && boxSelectionEnabled"
          v-model="activeTab"
          flat
          non-linear
          :alt-labels="isMobile"
          class="pa-0 preparation-stepper"
        >
          <v-stepper-header>
            <v-stepper-step
              step="1"
              :complete="packingTabUnlocked"
              :color="packingTabUnlocked ? 'black' : 'primary'"
              :style="packingTabUnlocked ? 'cursor: pointer' : ''"
              @click="backToStep1()"
            >
              <span
                class="ml-2"
                :style="isMobile ? 'font-size: .75rem' : ''"
                :class="activeTab === 1 ? 'primary--text' : ''"
              >
                {{ $t('subOrder.tabPicking') }}
              </span>
            </v-stepper-step>
            <v-divider />
            <v-stepper-step step="2" :editable="packingTabUnlocked" color="primary">
              <span
                class="ml-2"
                :style="isMobile ? 'font-size: .75rem' : ''"
                :class="activeTab === 2 ? 'primary--text' : ''"
              >
                {{ $t('subOrder.tabPacking') }}
              </span>
            </v-stepper-step>
          </v-stepper-header>
        </v-stepper>

        <!-- Product lists: picking tab OR box selection not active -->
        <template v-if="!isScanStep || !boxSelectionEnabled || activeTab === 1">
          <product-list-mobile
            v-if="isMobile"
            :subOrder="subOrder"
            :isScanStep="isScanStep"
            :isStockLoading="isStockLoading"
            :customizationsPanel="customizationsPanel"
            @open-product-position-modal="removeEventKeyDown"
            :displayProductsCustomCancelReason="displayProductsCustomCancelReason"
            :sourceLocation="sourceLocation"
            :itemCancelReasons="itemCancelReasons"
            @destroy-product-list="productListDestroyed"
            :displayActions="!subOrderPickPackShip && !loadingPickPackShip"
          />
          <product-list
            v-else-if="listDisplay"
            :subOrder="subOrder"
            :isScanStep="isScanStep"
            :isStockLoading="isStockLoading"
            :customizationsPanel="customizationsPanel"
            @open-product-position-modal="removeEventKeyDown"
            :displayProductsCustomCancelReason="displayProductsCustomCancelReason"
            :sourceLocation="sourceLocation"
            :itemCancelReasons="itemCancelReasons"
            @destroy-product-list="productListDestroyed"
            :displayActions="!subOrderPickPackShip && !loadingPickPackShip"
          />
          <product-card-display
            v-else
            :subOrder="subOrder"
            :isScanStep="isScanStep"
            :isStockLoading="isStockLoading"
            :customizationsPanel="customizationsPanel"
            @open-product-position-modal="removeEventKeyDown"
            :displayProductsCustomCancelReason="displayProductsCustomCancelReason"
            :itemCancelReasons="itemCancelReasons"
            @destroy-product-list="productListDestroyed"
            :displayActions="!subOrderPickPackShip && !loadingPickPackShip"
          />
        </template>

        <!-- Packing content: box selection active + on packing tab -->
        <packing-product-list
          v-else
          :sub-order="subOrder"
          :is-scan-step="isScanStep"
          :item-box-selections="itemBoxSelections"
          @open-box-selection="openBoxSelectionDialog"
        />

        <v-card-text v-if="subOrderPickPackShip" :class="isMobile ? 'error--text error-text-mobile' : 'error--text'" align="center">
          {{ alreadyInPickPackShipMessage }}
        </v-card-text>
        <span v-else-if="!loadingPickPackShip">
        <v-row v-if="mustPickALocker" justify="center">
          <v-btn class="v-btn-footer" dark depressed tile :disabled="loadingBtn" @click="lockerSelectionModal = true">
            <span v-if="isScanStep">
              <span v-if="!subOrder.preparation_locker_id">{{ $t('subOrder.selectALocker') }}</span>
              <span v-else>{{ $t('subOrder.changeLocker') }}: {{ getPreparationLocker() }}</span>
            </span>
            <span v-if="isReceptionStep">
              <span v-if="!subOrder.reception_locker_id">{{ $t('subOrder.selectALocker') }}</span>
              <span v-else>{{ $t('subOrder.changeLocker') }}: {{ getReceptionLocker() }}</span>
            </span>
            <v-icon right dark>mdi-locker-multiple</v-icon>
          </v-btn>
        </v-row>
        <v-row
          v-if="
            subOrder.current_status === 'waiting' &&
            isStorePreparation &&
            (subOrder.source_location_id === currentStore || isCustomerService)
          "
          justify="center"
          class="mb-2"
        >
          <v-btn
            class="v-btn-footer"
            :large="!isMobile"
            :medium="isMobile"
            color="primary"
            depressed
            tile
            :loading="loadingBtn"
            :disabled="!allItemCheck || loadingBtn"
            @click="confirmQuantities('confirmed')"
          >
            {{ $t('subOrder.validArticlesAvailability') }}
          </v-btn>
        </v-row>
        <v-row v-else-if="isScanStep" justify="center" class="mb-2 mt-3">
          <v-col>
            <!-- Picking step button -->
            <template v-if="!boxSelectionEnabled || activeTab === 1">
              <v-row justify="center" class="mb-2 text-center no-transform">
                {{ $t('subOrder.scanOrForceValidation') | capitalize }}
              </v-row>
              <v-row justify="center">
                <v-btn
                  class="v-btn-footer"
                  :large="!isMobile"
                  :medium="isMobile"
                  color="primary"
                  depressed
                  tile
                  :disabled="
                    loadingBtn ||
                    (!enableFastPreparationButton && !allProductAreScanned) ||
                    (mustPickALocker && !subOrder.preparation_locker_id) ||
                    !productsPositionsAreSelected
                  "
                  :loading="loadingBtn"
                  @click="boxSelectionEnabled ? goToPacking() : checkIncompleteScan()"
                >
                  {{ boxSelectionEnabled ? $t('subOrder.goToPacking') : $t('subOrder.validatePreparation') }}
                </v-btn>
              </v-row>
            </template>
            <!-- Packing step button -->
            <template v-else>
              <v-alert
                v-if="!allItemsHaveBoxSelection"
                type="warning"
                dense
                text
                :class="isMobile ? 'mb-8 caption' : 'mb-8'"
              >
                {{ $t('subOrder.boxSelectionRequired') }}
              </v-alert>
              <v-row justify="center">
                <v-btn
                  class="v-btn-footer"
                  :large="!isMobile"
                  :medium="isMobile"
                  color="primary"
                  depressed
                  tile
                  :disabled="!allItemsHaveBoxSelection || loadingBtn"
                  :loading="loadingBtn"
                  @click="validatePackingStep()"
                >
                  {{ $t('subOrder.validatePreparation') }}
                </v-btn>
              </v-row>
            </template>
          </v-col>
        </v-row>
        <v-row v-else-if="isReceptionStep">
          <v-col>
            <v-row justify="center">
              <v-btn
                class="v-btn-footer"
                :large="!isMobile"
                :medium="isMobile"
                color="primary"
                depressed
                tile
                :disabled="loadingBtn || (mustPickALocker && !subOrder.reception_locker_id)"
                :loading="loadingBtn"
                @click="validateClosure()"
              >
                {{ $t('subOrder.validateReception') }}
              </v-btn>
            </v-row>
          </v-col>
        </v-row>
        <v-row
          v-else-if="
            ['cancelled', 'partially_delivered'].includes(subOrder.current_status) && subOrder.put_back_in_stock
          "
        >
          <v-col>
            <v-row justify="center">
              <v-btn
                class="v-btn-footer"
                :large="!isMobile"
                :medium="isMobile"
                color="primary"
                depressed
                tile
                :disabled="loadingBtn"
                :loading="loadingBtn"
                @click="confirmPutBackInStockDialog = true"
              >
                {{ $t('subOrder.validatePutBackInStock') }}
              </v-btn>
            </v-row>
          </v-col>
        </v-row>
        <v-row
          v-if="
            subOrder.current_status === 'cancelled' &&
            ['customer_did_not_collect_his_reservation', 'customer_did_not_collect_order'].includes(
              subOrder.cancel_reason
            ) &&
            subOrder.put_back_in_stock
          "
        >
          <v-col>
            <v-row justify="center">
              <v-btn
                text
                :large="!isMobile"
                :medium="isMobile"
                color="secondary"
                depressed
                tile
                :disabled="loadingBtn"
                :loading="loadingBtn"
                @click="changeStatusDialog = true"
              >
                <U>{{ $t('subOrder.changeOrderStatus') }}</U>
              </v-btn>
            </v-row>
          </v-col>
        </v-row>
        <v-row
          v-if="
            [
              'waiting',
              'confirmed',
              'partially_confirmed',
              'waiting_carrier',
              'shipping_in_progress',
              'partial_shipping_in_progress',
              'partially_delivered',
              'delivered',
            ].includes(subOrder.current_status)
          "
        >
          <v-col v-if="this.subOrder.source_location_id === this.currentStore || this.isCustomerService">
            <div
              v-if="
                [
                  'waiting_carrier',
                  'shipping_in_progress',
                  'partial_shipping_in_progress',
                  'partially_delivered',
                  'delivered',
                ].includes(subOrder.current_status) && ['ship_from_store', 'inter_store'].includes(subOrder.type)
              "
            >
              <div class="text-center mb-2">
                <v-btn :loading="slipLoading" @click="checkDeliveryLabelGeneration()" dark depressed tile>
                  <span v-if="subOrder.current_status === 'waiting_carrier'">
                    {{ $t('subOrder.printShippingLabel') }}
                  </span>
                  <span v-else>
                    {{ $t('subOrder.reprintShippingLabel') }}
                  </span>
                  <v-icon right dark>mdi-cloud-print-outline</v-icon>
                </v-btn>
              </div>
              <div v-if="subOrder.current_status === 'waiting_carrier'">
                <div v-if="displayDeliverPackageToTransporterButton" class="text-center mt-2">
                  <v-btn
                    :large="!isMobile"
                    :medium="isMobile"
                    color="primary"
                    depressed
                    tile
                    :disabled="loadingBtn"
                    :loading="loadingBtn"
                    @click="confirmCarrierPickup()"
                  >
                    {{ $t('subOrder.PackageDeliveredToTransporter') }}
                  </v-btn>
                </div>
                <div class="text-center my-2">
                  <v-btn
                    :large="!isMobile"
                    :medium="isMobile"
                    :loading="cancellation"
                    :disabled="cancellation"
                    color="secondary"
                    outlined
                    tile
                    :dark="isDark"
                    @click="cancelSubOrderDialog = true"
                  >
                    {{ $t('subOrder.cancelPreparation') }}
                  </v-btn>
                </div>
              </div>
            </div>
            <div
              v-else-if="!['delivered', 'partially_delivered'].includes(subOrder.current_status)"
              class="text-center"
            >
              <v-btn
                v-if="isStorePreparation"
                :large="!isMobile"
                :medium="isMobile"
                :loading="cancellation"
                :disabled="cancellation"
                color="secondary"
                outlined
                tile
                @click="cancelSubOrderDialog = true"
                class="mb-3"
              >
                {{ $t('subOrder.refusePreparation') }}
              </v-btn>
            </div>
          </v-col>
          <v-col v-else-if="!this.isCustomerService && subOrder.current_status !== 'delivered'">
            <v-row justify="center">
              <v-card-title
                v-if="['in_store_pickup', 'ship_from_warehouse'].includes(subOrder.type)"
                class="text-center font-weight-bold subtitle-1"
              >
                {{ $t('subOrder.waitingWarehousePreparation') }}
              </v-card-title>
              <v-card-title
                v-if="['inter_store'].includes(subOrder.type) && this.subOrder.source_location_id !== this.currentStore"
                class="text-center font-weight-bold subtitle-1"
              >
                {{ $t('subOrder.waitingStorePreparation') }}
              </v-card-title>
            </v-row>
          </v-col>
        </v-row>
        <v-row v-if="subOrder.current_status === 'waiting_allocation'">
          <v-col>
            <v-row justify="center" class="mb-2">
              <v-btn
                class="v-btn-footer"
                v-if="isCustomerService"
                :large="!isMobile"
                :medium="isMobile"
                color="primary"
                depressed
                tile
                :disabled="loadingBtn"
                :loading="loadingBtn"
                @click="showPickLocationDialog = true"
              >
                {{ $t('subOrder.allocateToLocation') }}
              </v-btn>
              <v-btn
                class="v-btn-footer"
                v-else
                :large="!isMobile"
                :medium="isMobile"
                color="primary"
                depressed
                tile
                :disabled="loadingBtn"
                :loading="loadingBtn"
                @click="takeInCharge(currentStore)"
              >
                {{ $t('subOrder.takeInCharge') }}
              </v-btn>
            </v-row>
          </v-col>
        </v-row>
        <generic-expandable-list
          v-if="mustDisplayTrackingReferences"
          :items="subOrder.shipments"
          url-field="item.tracking_url"
          reference-field="item.tracking_reference"
          :panel-text-hide="$t('shipment.hideTrackingReference')"
          :panel-text-show="$t('shipment.displayTrackingReference')"
        >
          <template #left="{ item }">
            <v-icon small class="ml-2 copy-icon" @click="copyToClipboard(item.tracking_reference)">
              mdi-content-copy
            </v-icon>
          </template>
        </generic-expandable-list>
        <tracking-list v-if="mustDisplayTrackingReferences" :shipments="subOrder.shipments" />
        </span>
        <customer-details-bar
          v-if="mustDisplayCustomerInformation"
          :customer="subOrder.customer"
          :shippingAddress="subOrder.shipping_address"
          :billingAddress="subOrder.billing_address"
          :canModifyAddress="displayAddressEditButtons"
          :canModifyCustomer="displayCustomerEditButtons"
          :order_id="subOrder.order_id"
          @validateForm="validateAddressForm"
          @validateCustomerForm="validateCustomerForm"
        ></customer-details-bar>
      </v-container>
      <!--{{subOrder}}-->
    </v-card>
    <customer-delivery-dialog
      v-if="customerDeliveryDialog"
      v-model="customerDeliveryDialog"
      @validateCustomerDelivery="validateCustomerDelivery($event)"
      :item="targetItem"
    ></customer-delivery-dialog>
    <cancel-sub-order-dialog
      v-model="cancelSubOrderDialog"
      :choices="cancelReasons"
      :title="$t('subOrder.cancelReasonTitle')"
      :introductionSentence="$t('subOrder.cancelReasonText')"
      @confirm="cancel"
    ></cancel-sub-order-dialog>
    <cancel-sub-order-dialog
      v-model="changeStatusDialog"
      :choices="changeOrderStatusReasons"
      :title="$t('subOrder.changeOrderStatus')"
      :introductionSentence="$t('subOrder.changeOrderStatusText')"
      @confirm="changeOrderStatus"
    ></cancel-sub-order-dialog>
    <barecode-type-dialog
      :title="$t('product.enterYourBarcode')"
      v-model="showBarecodeDialog"
      @validate="validateBarcodeDialog"
      @close="addEventKeyDown"
    />
    <confirm-incomplete-scan-dialog
      v-model="confirmIncompleteScanDialog"
      @yes="boxSelectionEnabled ? unlockPackingAfterConfirm() : validateClosure()"
      @no="confirmIncompleteScanDialog = false"
      :title="$t('subOrder.validPreparation')"
      :text="$t('subOrder.productsAreNotAllScanned')"
    />
    <pick-location-dialog
      v-model="showPickLocationDialog"
      :title="$t('subOrder.cancelReasonTitle')"
      :introductionSentence="$t('subOrder.cancelReasonText')"
      :orderForManualAllocation="subOrder"
      :pickupLocationId="subOrder.pickup_location_id"
      @confirm="takeInCharge"
    />
    <confirmation-carrier-pickup
      v-model="showConfirmCarrierPickupDialog"
      @confirm="validateCarrierPickup()"
      @close="closeCarrierPickupModal()"
    ></confirmation-carrier-pickup>
    <confirm-delivery-label
      v-model="deliveryLabelModal"
      @yes="doPrintTransporterEtiquette()"
      @no="deliveryLabelModal = false"
      :title="$t('subOrder.validDeliveryLabelGen')"
      :text="$t('subOrder.validDeliveryLabelGenText')"
    />
    <spread-item-scan-dialog
      v-model="spreadItemScanDialog"
      :title="$t('product.customVariant')"
      :introductionSentence="$t('product.customVariantText')"
    ></spread-item-scan-dialog>
    <locker-selection-dialog
      v-model="lockerSelectionModal"
      :lockerId="isScanStep ? subOrder.preparation_locker_id : subOrder.reception_locker_id"
      @close="lockerSelectionModal = false"
      @validate="validateLocker"
    ></locker-selection-dialog>
    <confirm-put-back-in-stock-dialog
      v-model="confirmPutBackInStockDialog"
      @yes="validatePutBackInStock()"
      @no="confirmPutBackInStockDialog = false"
      :title="$t('subOrder.confirmPutBackInStock')"
      :text="$t('subOrder.confirmPutBackInStockText')"
    ></confirm-put-back-in-stock-dialog>
    <!-- Box selection dialog -->
    <box-selection-dialog
      v-if="boxSelectionEnabled"
      v-model="boxSelectionDialog"
      :item="currentBoxItem"
      :box-type-items="boxTypeItems"
      :initial-entries="currentBoxItem ? itemBoxSelections[currentBoxItem._id] || [] : []"
      :is-mobile="isMobile"
      @save="onBoxSelectionSave"
      @close="onBoxSelectionClose"
    />
    <label-generation-error
      v-model="errorLabelGenerationModal"
      :errors="labelErrors"
      @close="errorLabelGenerationModal = false"
    ></label-generation-error>
    <template>
      <v-theme-provider light>
        <div>
          <vue-html2pdf
            :show-layout="false"
            :float-layout="true"
            :enable-download="true"
            :preview-modal="false"
            :paginate-elements-by-height="1400"
            :manual-pagination="false"
            pdf-content-width="100%"
            ref="html2Pdf"
            :html-to-pdf-options="htmlToPdfOptions"
            @startPagination="pdfLoading = true"
            @hasDownloaded="pdfLoading = false"
          >
            <section slot="pdf-content">
              <summary-pdf ref="SummaryPDF" :subOrder="subOrder" />
            </section>
          </vue-html2pdf>
        </div>
      </v-theme-provider>
    </template>
  </v-container>
</template>

<script>
  import { EventBus } from '../../event-bus'
  import { mapState, mapActions } from 'vuex'
  import CustomerDeliveryDialog from '@/components/SubOrders/CustomerDeliveryDialog.vue'
  import CancelSubOrderDialog from '@/components/MultipleChoicesDialog.vue'
  import CustomerDetailsBar from '@/components/CustomerDetailsBar.vue'
  import PickLocationDialog from '@/components/dialogs/PickLocationDialog.vue'
  import SimpleTextInputDialog from '@/components/dialogs/SimpleTextInputDialog'
  import confirmationDialog from '../../components/ConfirmationDialog'
  import informationDialog from '@/components/dialogs/InformationDialog'
  import VueHtml2pdf from 'vue-html2pdf'
  import SummaryPdf from '@/components/SummaryPdf'
  import ConfirmationCarrierPickup from '@/components/dialogs/ConfirmationCarrierPickup'
  import ProductListMobile from '@/components/products/ProductListMobile.vue'
  import ProductList from '@/components/products/ProductList.vue'
  import ProductCardDisplay from '@/components/products/ProductCardDisplay.vue'
  import OrderHeadersMobile from '@/components/orders/OrderHeadersMobile.vue'
  import OrderHeaders from '@/components/orders/OrderHeaders.vue'
  import lockerSelectionDialog from '@/components/dialogs/lockerSelectionDialog'
  import LabelGenerationError from '@/components/dialogs/LabelGenerationError.vue'
  import BoxSelectionDialog from '@/components/dialogs/BoxSelectionDialog.vue'
  import PackingProductList from '@/components/products/PackingProductList.vue'
  import GenericExpandableList from '@/components/GenericExpandableList.vue'
  import { displayMessage } from '@/helpers/helper'

  let customerPrintSlip = process.env.VUE_APP_CUSTOMER_PRINT_SLIP

  export default {
    name: 'subOrderDetails',
    components: {
      LabelGenerationError,
      CustomerDetailsBar,
      CustomerDeliveryDialog,
      CancelSubOrderDialog,
      PickLocationDialog,
      barecodeTypeDialog: SimpleTextInputDialog,
      confirmIncompleteScanDialog: confirmationDialog,
      VueHtml2pdf,
      SummaryPdf,
      ConfirmationCarrierPickup,
      ProductListMobile,
      ProductList,
      ProductCardDisplay,
      OrderHeadersMobile,
      OrderHeaders,
      confirmDeliveryLabel: confirmationDialog,
      spreadItemScanDialog: informationDialog,
      lockerSelectionDialog,
      confirmPutBackInStockDialog: confirmationDialog,
      GenericExpandableList,
      BoxSelectionDialog,
      PackingProductList,
    },
    data() {
      return {
        subOrder: null,
        scan: '',
        printInvoice: false,
        printDeliveryForm: false,
        printReturnVoucher: false,
        targetItem: undefined,
        defaultImage: require('@/assets/img/products/product_default.png'),
        loadingBtn: false,
        cancellation: false,
        customerDeliveryDialog: false,
        cancelSubOrderDialog: false,
        confirmIncompleteScanDialog: false,
        listDisplay: true,
        showBarecodeDialog: false,
        printTransporterEtiquette: customerPrintSlip
          ? customerPrintSlip.includes(this.$store.state.currentGroup.name)
          : false,
        slipLoading: false,
        showPickLocationDialog: false,
        showConfirmCarrierPickupDialog: false,
        pdfLoading: false,
        orderSettings: {},
        stockIsLoading: false,
        deliveryLabelModal: false,
        customizationsPanel: [],
        spreadItemScanDialog: false,
        lockerSelectionModal: false,
        loadedStocks: [],
        confirmPutBackInStockDialog: false,
        changeStatusDialog: false,
        sourceLocation: undefined,
        errorLabelGenerationModal: false,
        labelErrors: [],
        itemCancelReasons: [],
        cancelReasons: [],
        packingTabUnlocked: false,
        activeTab: 1,
        itemBoxSelections: {},
        boxSelectionDialog: false,
        currentBoxItem: null,
        subOrderPickPackShip: undefined,
        loadingPickPackShip: false,
      }
    },
    mounted() {
      if (this.$route.params.id) {
        if (
          this.currentStoreDetails &&
          this.currentStoreDetails.address &&
          this.currentStoreDetails.address.country_id
        ) {
          this.getSettingsByCountry(this.currentStoreDetails.address.country_id)
        } else if (this.isCustomerService) {
          // get settings temporary fix
          this.getSettingsByCountry('FRA')
        }
      }
    },
    beforeDestroy() {
      this.removeEventKeyDown()
    },
    computed: {
      ...mapState([
        'currentStore',
        'isCustomerService',
        'isDark',
        'isAdmin',
        'breadcrumbs',
        'currentStoreDetails',
        'partnerSettings',
        'roles',
        'isOctipasAdmin',
      ]),
      isMobile() {
        return this.$vuetify.breakpoint.width < 650
      },
      isHomeDelivery: function () {
        return this.subOrder.type === 'ship_from_warehouse' || this.subOrder.type === 'ship_from_store'
      },
      allItemCheck: function () {
        let allItemCheck = true
        for (let item of this.subOrder.items) {
          if (!item.current_status) {
            allItemCheck = false
            break
          }
        }
        return allItemCheck
      },
      computedItems: function () {
        if (this.subOrder.current_status === 'partially_confirmed') {
          return this.subOrder.items.filter((item) => item.current_status !== 'cancelled')
        } else {
          return this.subOrder.items
        }
      },
      nbProduct: function () {
        let nbProduct = 0
        if (!['waiting', 'cancelled', 'waiting_allocation'].includes(this.subOrder.current_status)) {
          for (let item of this.computedItems) {
            if (item.current_status === 'validated') {
              nbProduct += item.product_quantity
            } else {
              if (item.validated_quantity && item.current_status !== 'cancelled') {
                nbProduct += item.validated_quantity
              }
            }
          }
        } else {
          for (let item of this.computedItems) {
            nbProduct += item.product_quantity
          }
        }
        return nbProduct
      },
      isStorePreparation: function () {
        return ['click_and_collect', 'click_and_reserve', 'ship_from_store', 'inter_store'].includes(this.subOrder.type)
      },
      isScanStep: function () {
        if (this.subOrder) {
          let isScanStep =
            ['partially_confirmed', 'confirmed'].includes(this.subOrder.current_status) &&
            this.isStorePreparation &&
            (this.subOrder.source_location_id === this.currentStore || this.isCustomerService)
          if (isScanStep) {
            this.addEventKeyDown()
          } else {
            this.removeEventKeyDown()
          }
          return isScanStep
        } else return false
      },
      htmlToPdfOptions() {
        return {
          margin: 0,
          filename: `${this.$t('subOrder.deliverySlip')} - ${this.subOrder.order_id}`,
          html2canvas: {
            scale: 4,
            dpi: 300,
            useCORS: true,
            letterRendering: true,
            imageTimeout: 0,
          },
          jsPDF: {
            unit: 'in',
            format: 'a4',
            orientation: 'portrait',
          },
        }
      },
      displayDeliverPackageToTransporterButton: function () {
        return (
          this.isAdmin ||
          !this.orderSettings ||
          !this.orderSettings.display ||
          this.orderSettings.display.display_deliver_package_to_transporter_button !== false
        )
      },
      enableFastPreparationButton: function () {
        return (
          this.isAdmin ||
          !this.orderSettings ||
          !this.orderSettings.display ||
          this.orderSettings.display.display_fast_preparation_button !== false
        )
      },
      allProductAreScanned() {
        let allProductAreScanned = true
        for (let item of this.computedItems) {
          if (!this.itemIsEntirelyScanned(item, item.nb_scanned)) {
            allProductAreScanned = false
          }
        }
        return allProductAreScanned
      },
      isStockLoading() {
        return this.stockIsLoading
      },
      isReceptionStep() {
        return (
          ['in_store_pickup', 'inter_store'].includes(this.subOrder.type) &&
          ['shipping_in_progress', 'partial_shipping_in_progress'].includes(this.subOrder.current_status) &&
          (this.subOrder.pickup_location_id === this.currentStore || this.isCustomerService)
        )
      },
      mustPickALocker() {
        let lockerSelectionActivated = false
        if (this.orderSettings?.delivery?.locker) {
          if (this.isScanStep) {
            lockerSelectionActivated =
              this.orderSettings.delivery.locker.for_preparation?.activate &&
              this.orderSettings.delivery.locker.for_preparation?.order_types.includes(this.subOrder.type)
          } else if (this.isReceptionStep) {
            lockerSelectionActivated =
              this.orderSettings.delivery.locker.for_reception?.activate &&
              this.orderSettings.delivery.locker.for_reception?.order_types.includes(this.subOrder.type)
          }
        }
        return lockerSelectionActivated
      },
      mustDisplayAnyLocker() {
        const preparationLockerId = this.subOrder.preparation_locker_id
        const selectedPreparationLocker = preparationLockerId && preparationLockerId !== 'no_available_locker'
        const receptionLockerSelected =
          this.subOrder.reception_locker_id && this.subOrder.reception_locker_id !== 'no_available_locker'
        const deliveredSubOrder = ['delivered', 'partially_delivered'].includes(this.subOrder.current_status)
        return (selectedPreparationLocker || receptionLockerSelected) && !deliveredSubOrder
      },
      mustDisplayPreparationLocker() {
        const preparationLockerId = this.subOrder.preparation_locker_id
        const selectedPreparationLocker = preparationLockerId && preparationLockerId !== 'no_available_locker'
        const isNotInterStoreToPickup =
          this.subOrder.current_status === 'to_pickup' && this.subOrder.type !== 'inter_store'
        const isNotSentPreparation = ['confirmed', 'partially_confirmed', 'waiting_carrier'].includes(
          this.subOrder.current_status
        )
        return selectedPreparationLocker && (isNotSentPreparation || isNotInterStoreToPickup)
      },
      productsPositionsAreSelected() {
        let productsPositionsAreSelected = true
        for (let item of this.subOrder.items) {
          if (this.mustPickupProductLocation(item.product_positions) && !this.productIsFullyPicked(item)) {
            productsPositionsAreSelected = false
          }
        }
        return productsPositionsAreSelected
      },
      mustDisplayCustomerInformation() {
        let displayCustomerInformation = true
        if (
          !this.subOrder.billing_address &&
          !this.subOrder.shipping_address &&
          !this.subOrder.customer.email &&
          !this.subOrder.customer.last_name &&
          !this.subOrder.customer.first_name &&
          !this.subOrder.customer.phone &&
          !this.subOrder.customer.last_four_digit_phone_number
        ) {
          displayCustomerInformation = false
        }
        return displayCustomerInformation
      },
      displayProductsCustomCancelReason: function () {
        return this.orderSettings?.display?.display_products_custom_cancel_reason
      },
      changeOrderStatusReasons() {
        return [{ text: this.$t('subOrder.PackageDeliveredToCustomer'), value: 'package_delivered_to_customer' }]
      },
      displayAddressEditButtons: function () {
        return this.isOctipasAdmin || this.orderSettings.display?.display_address_edit_buttons.includes(this.roles[0])
      },
      displayCustomerEditButtons: function () {
        return this.isOctipasAdmin || this.orderSettings.display?.display_customer_edit_buttons.includes(this.roles[0])
      },
      mustDisplayTrackingReferences() {
        return Array.isArray(this.subOrder?.shipments) && this.subOrder.shipments.length
      },
      boxSelectionEnabled() {
        return (
          !!this.orderSettings?.preparation?.box_selection?.activate &&
          !!this.orderSettings.preparation.box_selection.order_types?.includes(this.subOrder?.type)
        )
      },
      activeBoxTypes() {
        return (this.orderSettings?.preparation?.box_types || []).filter((b) => b.activate)
      },
      boxTypeItems() {
        const locale = this.$i18n.locale
        const boxes = this.activeBoxTypes.map((b) => ({
          ...b,
          name: (b.labels && (b.labels[locale] || b.labels.en)) || b.reference,
        }))
        return boxes
      },
      allItemsHaveBoxSelection() {
        return this.computedItems
          .filter((item) => item.current_status !== 'cancelled')
          .every((item) => {
            const entries = this.itemBoxSelections[item._id]
            return entries && entries.length > 0
          })
      },
      alreadyInPickPackShipMessage() {
        if (this.subOrderPickPackShip) {
          const hasPicker = this.subOrderPickPackShip.picker
          const translationKey = hasPicker ? 'subOrder.alreadyInPickPackShip' : 'subOrder.alreadyInPickPackShipNoPicker'
          const replacingParameters = { picker: hasPicker || undefined, reference: this.subOrderPickPackShip.reference }
          return this.$t(translationKey, replacingParameters)
        }
        return ''
      },
    },
    methods: {
      ...mapActions(['updateBreadcrumb']),
      async get() {
        const path = '/v1/int/sub-orders/' + this.$route.params.id
        const myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        try {
          const res = await this.$Amplify.API.get('sub-orders', path, myInit)
          const displayStockStatus = ['waiting', 'confirmed', 'waiting_allocation']
          const typesWithWarehousePreparation = ['in_store_pickup', 'ship_from_warehouse']
          if (
            this.orderSettings?.display?.display_stock_indicator_on_product &&
            displayStockStatus.includes(res.current_status) &&
            !typesWithWarehousePreparation.includes(res.type)
          ) {
            this.stockIsLoading = true
            this.getStock(res)
          }
          this.subOrder = res
          this.customizationsPanel = this.subOrder.items.map(() => [0])
          if (this.subOrder.source_location_id) {
            this.getSourceLocation(this.subOrder.source_location_id)
          }
          this.getSubOrderPickPackShip()
        } catch (e) {
          console.log(e)
        }
      },
      async patchSubOrder(patchData, isSimpleUpdate = false) {
        this.loadingBtn = true
        const dateLimitBeforeTimeoutReached =
          this.subOrder.date_limit_before_timeout && new Date() > new Date(this.subOrder.date_limit_before_timeout)
        if (
          ['waiting', 'confirmed', 'partially_confirmed'].includes(this.subOrder.current_status) &&
          dateLimitBeforeTimeoutReached
        ) {
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('subOrder.deadlineReached'),
            color: 'error',
          })
          return
        }

        const path = '/v1/int/sub-orders/' + this.$route.params.id
        patchData.simpleUpdate = isSimpleUpdate
        const myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          body: patchData,
        }

        try {
          const res = await this.$Amplify.API.patch('sub-orders', path, myInit)
          this.subOrder = res
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('subOrder.orderUpdated'),
            color: 'success',
          })
          this.cancellation = false

          if (
            ['to_pickup', 'cancelled', 'partially_delivered'].includes(this.subOrder.current_status) &&
            !isSimpleUpdate
          ) {
            if (this.isAdmin) {
              await this.$router.push('/orders')
            } else {
              await this.$router.push('/sub-orders/waiting')
            }
          } else if (this.$route.params.id !== this.subOrder.order_id) {
            const breadCrumbsIndex = this.breadcrumbs.findIndex((item) => item.to === this.$route.path)
            await this.updateBreadcrumb({
              index: breadCrumbsIndex,
              value: {
                text: this.breadcrumbs[breadCrumbsIndex].text.replace(this.$route.params.id, this.subOrder.order_id),
                to: '/sub-orders/' + this.subOrder.order_id,
              },
            })
            const displayStockStatus = ['waiting', 'confirmed', 'waiting_allocation']
            const typesWithWarehousePreparation = ['in_store_pickup', 'ship_from_warehouse']
            if (
              this.orderSettings?.display?.display_stock_indicator_on_product &&
              displayStockStatus.includes(res.current_status) &&
              !typesWithWarehousePreparation.includes(res.type)
            ) {
              this.stockIsLoading = true
              await this.getStock(res)
            }
            if (this.subOrder.source_location_id) {
              await this.getSourceLocation(this.subOrder.source_location_id)
            }
            await this.$router.push('/sub-orders/' + this.subOrder.order_id)
          } else {
            EventBus.$emit('reLoadCounter')
            this.dispatchStocks(this.subOrder)
          }
        } catch (e) {
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('subOrder.errorUpdate'),
            color: 'error',
          })
          if (e.response?.data?.subOrder) {
            this.subOrder = e.response.data.subOrder
          }
          this.cancellation = false
        } finally {
          this.loadingBtn = false
        }
      },
      confirmQuantities: function (newStatus) {
        let allItemsAreCancelled = true
        for (let item of this.subOrder.items) {
          if (item.current_status !== 'cancelled') {
            allItemsAreCancelled = false
          }
        }
        if (allItemsAreCancelled) {
          this.cancelSubOrderDialog = true
        } else {
          let patchData = {
            current_status: newStatus,
            items: this.subOrder.items,
          }
          this.patchSubOrder(patchData)
        }
      },
      cancel: function (cancelReason) {
        let patchData = {
          current_status: 'cancelled',
          cancel_reason: cancelReason,
          items: this.subOrder.items,
        }
        this.cancellation = true
        this.patchSubOrder(patchData)
      },
      validateBarcodeDialog: function (ean) {
        this.addEventKeyDown()
        this.checkItemsForScan(ean)
      },
      validateCustomerDelivery: function () {
        let patchData = {
          current_status: 'delivered',
          items: this.subOrder.items,
        }
        this.patchSubOrder(patchData)
        this.customerDeliveryDialog = false
      },
      validateClosure: function () {
        let patchData = {
          items: this.subOrder.items,
        }

        if (
          ['inter_store', 'ship_from_store'].includes(this.subOrder.type) &&
          ['confirmed', 'partially_confirmed'].includes(this.subOrder.current_status)
        ) {
          patchData.current_status = 'waiting_carrier'
        } else {
          patchData.current_status = 'to_pickup'
        }

        if (Array.isArray(this.subOrder.custom) && this.subOrder.custom.length) {
          patchData.custom = this.subOrder.custom
        }

        if (this.subOrder.preparation_locker_id) {
          patchData.preparation_locker_id = this.subOrder.preparation_locker_id
        }

        if (this.subOrder.reception_locker_id) {
          patchData.reception_locker_id = this.subOrder.reception_locker_id
        }

        if (this.boxSelectionEnabled) {
          const parcels = []
          for (const item of this.computedItems) {
            const entries = this.itemBoxSelections[item._id] || []
            for (const entry of entries) {
              parcels.push({
                box_reference: entry.boxType.reference,
                box_labels: entry.boxType.labels || {},
                length: entry.boxType.dimensions?.length,
                width: entry.boxType.dimensions?.width,
                height: entry.boxType.dimensions?.height,
                weight: entry.boxType.empty_weight || undefined,
                items: [
                  {
                    item_id: item.item_id,
                    product_id: item.product_id,
                    quantity: entry.quantity,
                  },
                ],
              })
            }
          }
          if (parcels.length) {
            patchData.parcels = parcels
          }
        }

        this.patchSubOrder(patchData)
        this.confirmIncompleteScanDialog = false
      },
      checkDeliveryLabelGeneration() {
        if (Array.isArray(this.subOrder.shipments) && this.subOrder.shipments.length) {
          if (this.subOrder.shipments[0].shipping_label_generation_date) {
            this.deliveryLabelModal = true
          } else {
            this.doPrintTransporterEtiquette()
          }
        } else {
          this.doPrintTransporterEtiquette()
        }
      },
      async doPrintTransporterEtiquette() {
        this.deliveryLabelModal = false
        const path = `/v1/int/sub-orders/${this.subOrder.order_id}/slips`
        const myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        this.slipLoading = true
        try {
          const res = await this.$Amplify.API.post('sub-orders', path, myInit)
          const link = document.createElement('a')
          link.href = res.fileLocation
          if (!this.isMobile) {
            link.target = '_blank'
          }
          document.body.appendChild(link)
          link.click()
          this.slipLoading = false
          await this.get()
        } catch (e) {
          console.log(e)
          EventBus.$emit('displaySnackbar', {
            display: true,
            color: 'error',
            text:
              e.response.status === 404
                ? this.$t('subOrder.shippingLabelNotFound')
                : 'Error during generating slip order',
          })
          if (e.response?.data?.errors?.length) {
            this.labelErrors = e.response.data.errors
            this.errorLabelGenerationModal = true
          }
          this.slipLoading = false
        }
      },
      // scan functions
      openBarcodeDialog: function () {
        this.removeEventKeyDown()
        this.showBarecodeDialog = true
      },
      checkIncompleteScan() {
        if (this.isScanStep && !this.allProductAreScanned && this.checkItemForEan()) {
          this.confirmIncompleteScanDialog = true
        } else {
          this.validateClosure()
        }
      },
      backToStep1() {
        if (!this.packingTabUnlocked) {
          return
        }
        this.activeTab = 1
      },
      goToPacking() {
        if (this.isScanStep && !this.allProductAreScanned && this.checkItemForEan()) {
          this.confirmIncompleteScanDialog = true
        } else {
          this.packingTabUnlocked = true
          this.activeTab = 2
        }
      },
      unlockPackingAfterConfirm() {
        this.confirmIncompleteScanDialog = false
        this.packingTabUnlocked = true
        this.activeTab = 2
      },
      openBoxSelectionDialog(item) {
        this.currentBoxItem = item
        this.boxSelectionDialog = true
      },
      onBoxSelectionSave(entries) {
        this.$set(this.itemBoxSelections, this.currentBoxItem._id, entries)
        this.boxSelectionDialog = false
        this.currentBoxItem = null
      },
      onBoxSelectionClose() {
        this.boxSelectionDialog = false
        this.currentBoxItem = null
      },
      validatePackingStep() {
        if (!this.allItemsHaveBoxSelection) {
          return
        }
        this.validateClosure()
      },
      addEventKeyDown: function () {
        document.addEventListener('keydown', this.convertSpecialCharToNumber)
      },
      removeEventKeyDown: function () {
        document.removeEventListener('keydown', this.convertSpecialCharToNumber)
      },
      convertSpecialCharToNumber: function (event) {
        const charCode = event.charCode ? event.charCode : event.keyCode
        if (charCode >= 48 && charCode <= 57) {
          this.scan += String.fromCharCode(charCode)
        } else if (charCode === 13 && this.scan.trim() !== '') {
          this.checkItemsForScan(this.scan)
          this.scan = ''
        }
      },
      checkItemsForScan(ean) {
        let productExists = false
        for (let item of this.subOrder.items) {
          let barcode = String(item.barcode || item.product_ean)
          if (barcode === ean) {
            if (this.getItemOccurrences(barcode) > 1) {
              this.spreadItemScanDialog = true
              return
            }
            if (item.current_status === 'cancelled') {
              console.log(this.$t('product.productPreviouslyCancelled'))
              EventBus.$emit('displaySnackbar', {
                display: true,
                text: this.$t('product.productPreviouslyCancelled'),
                color: 'error',
              })
            } else {
              EventBus.$emit('displaySnackbar', {
                display: true,
                text: this.$t('product.productScanned'),
                color: 'success',
              })
              if (item.nb_scanned) {
                if (!this.itemIsEntirelyScanned(item, item.nb_scanned)) {
                  item.nb_scanned++
                } else {
                  EventBus.$emit('displaySnackbar', {
                    display: true,
                    text: this.$t('product.productAlreadyScanned'),
                    color: 'error',
                  })
                }
              } else {
                this.$set(item, 'nb_scanned', 1)
              }
            }
            productExists = true
            break
          }
        }
        if (!productExists) {
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('product.productScannedError'),
            color: 'error',
          })
        }
      },
      itemIsEntirelyScanned(item, nbScanned) {
        const qtyScanned = nbScanned ? nbScanned : 0
        return (
          (item.current_status === 'partially_validated' && qtyScanned >= item.validated_quantity) ||
          (item.current_status === 'validated' && qtyScanned >= item.product_quantity)
        )
      },

      takeInCharge(location) {
        if (typeof location == 'string') {
          this.subOrder.source_location_id = location
        } else if (location.location_id) {
          this.subOrder.source_location_id = location.location_id
        }
        let patchData = {
          current_status: 'waiting',
          items: this.subOrder.items,
          source_location_id: this.subOrder.source_location_id,
        }
        this.patchSubOrder(patchData)
      },
      downloadPDF() {
        this.$refs.html2Pdf.generatePdf()
      },
      confirmCarrierPickup() {
        this.showConfirmCarrierPickupDialog = true
      },
      closeCarrierPickupModal() {
        this.showConfirmCarrierPickupDialog = false
      },
      validateCarrierPickup: function () {
        this.patchSubOrder({ current_status: 'shipping_in_progress', items: this.subOrder.items })
        this.showConfirmCarrierPickupDialog = false
      },
      checkItemForEan() {
        let itemsHaveEan = true
        for (let item of this.subOrder.items) {
          if (!item.product_ean && !item.barcode) {
            itemsHaveEan = false
            break
          }
        }
        return itemsHaveEan
      },
      validateAddressForm(values) {
        const fieldsToPatch = values.addressType
        this.$set(this.subOrder, fieldsToPatch, values.data)

        let patchData = {
          [fieldsToPatch]: { ...this.removeEmptyValue(values.data) },
        }

        this.patchSubOrder(patchData, true)
      },
      removeEmptyValue(form) {
        let newFormObject = {}
        for (let key of Object.keys(form)) {
          if (form[key] !== '') {
            newFormObject[key] = form[key]
          }
        }

        return newFormObject
      },
      async getSettingsByCountry(countryIso3) {
        // get the settings of the country selected
        this.loadingBtn = true
        const myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        try {
          const res = await this.$Amplify.API.get('settings', '/v1/settings/' + countryIso3, myInit)
          this.orderSettings = res
          this.setReasons(res)
          await this.get()
        } catch (e) {
          console.log(e)
          if (e.response?.status === 404 && countryIso3 !== 'FRA') {
            console.log(`No settings for country ${this.currentStoreDetails.address.country_id}`)
            await this.getSettingsByCountry('FRA')
          }
        } finally {
          this.loadingBtn = false
        }
      },
      async getStock(subOrder) {
        const productRefs = []
        subOrder.items.forEach((item) => {
          if (item.product_id) {
            productRefs.push(item.product_id)
          }
        })

        const unifiedStockInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            per_page: 1000,
            //no source location means it is a sub order not allocated
            location_id: subOrder.source_location_id ? subOrder.source_location_id : this.currentStore,
            product_id: productRefs,
            fields: ['stock_total', 'product_positions'],
          },
        }
        try {
          const res = await this.$Amplify.API.get('stocks', '/stocks', unifiedStockInit)
          this.loadedStocks = res.data
          this.dispatchStocks(subOrder)
        } catch (e) {
          console.log(e)
        } finally {
          this.stockIsLoading = false
        }
      },
      getStockColor(itemStock, itemQuantity) {
        let color = 'red'
        if (itemStock >= itemQuantity) {
          color = 'green'
        } else if (itemStock !== 0 && itemStock < itemQuantity) {
          color = 'orange'
        }
        return color
      },
      getItemOccurrences(barcode) {
        return this.subOrder.items.reduce((accumulator, currentValue) => {
          if (barcode === currentValue.barcode) {
            return accumulator + 1
          } else {
            return accumulator
          }
        }, 0)
      },
      validateLocker(lockerId) {
        if (this.isScanStep) {
          this.$set(this.subOrder, 'preparation_locker_id', lockerId)
        } else {
          this.$set(this.subOrder, 'reception_locker_id', lockerId)
        }
        this.lockerSelectionModal = false
      },
      getPreparationLocker() {
        const preparationLockerId = this.subOrder.preparation_locker_id
        return preparationLockerId === 'no_available_locker'
          ? this.$t('subOrder.noAvailableLocker')
          : preparationLockerId
      },
      getReceptionLocker() {
        const receptionLockerId = this.subOrder.reception_locker_id
        return receptionLockerId === 'no_available_locker' ? this.$t('subOrder.noAvailableLocker') : receptionLockerId
      },
      mustPickupProductLocation(productPositions) {
        return this.isScanStep && Array.isArray(productPositions) && productPositions.length > 0
      },
      productIsFullyPicked(product) {
        let productsIsFullyPicked = false
        if (Array.isArray(product.selected_product_positions)) {
          const productTakenQuantity = product.selected_product_positions.reduce(
            (productTakenQuantity, position) => parseInt(productTakenQuantity) + parseInt(position.taken_quantity),
            0
          )
          productsIsFullyPicked = productTakenQuantity === product.validated_quantity
        }
        return productsIsFullyPicked
      },
      productListDestroyed() {
        if (this.isScanStep) {
          this.addEventKeyDown()
        }
      },
      dispatchStocks(subOrder) {
        if (Array.isArray(this.loadedStocks)) {
          for (let stock of this.loadedStocks) {
            for (let item of subOrder.items) {
              if (item.product_id === stock.product_id) {
                // display stock_total because this stock exclude current reservation on item
                item.stock = stock.stock_total
                item.stock_color = this.getStockColor(stock.stock_total, item.product_quantity)
                if (Array.isArray(stock.product_positions)) {
                  item.product_positions = stock.product_positions.map((stockPosition) => {
                    return {
                      stock: stockPosition.stock_total,
                      stock_color: this.getStockColor(stockPosition.stock_total, item.product_quantity),
                      position_id: stockPosition.product_position_id,
                      position_details: stockPosition.custom,
                      barcode: stockPosition.product_position_barcode,
                    }
                  })
                }
              }
            }
          }
          this.$set(this.subOrder, subOrder)
        }
      },
      validatePutBackInStock: function () {
        this.patchSubOrder({ current_status: this.subOrder.current_status, put_back_in_stock: false })
        this.confirmPutBackInStockDialog = false
      },
      async changeOrderStatus(changeOrderStatusReason) {
        this.loadingBtn = true
        this.loadingData = true

        const patchData = {
          current_status: 'delivered',
          status_comment: changeOrderStatusReason,
        }
        const path = '/v1/int/orders/' + this.subOrder.source_order_id
        const myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          body: patchData,
        }
        try {
          const res = await this.$Amplify.API.patch('orders', path, myInit)
          this.subOrder = res.sub_orders.find((suborder) => suborder.order_id === this.subOrder.order_id)
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('subOrder.orderUpdated'),
            color: 'success',
          })
          this.cancellation = false
          EventBus.$emit('reLoadCounter')
        } catch (e) {
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('subOrder.errorUpdate'),
            color: 'error',
          })
          console.log(e)
          this.cancellation = false
        } finally {
          this.loadingBtn = false
        }
      },
      async getSourceLocation(location_id) {
        const myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            fields: ['location_id', 'name'],
          },
        }
        try {
          this.sourceLocation = await this.$Amplify.API.get('locations', '/v1/int/locations/' + location_id, myInit)
        } catch (e) {
          console.log(e)
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('orderWorkflow.locationsLoadingErrorMessage'),
            color: 'error',
          })
        }
      },
      setReasons(settings) {
        const cancelReasonsTranslations = [
          { text: this.$t('subOrder.clientChangeOfMind'), value: 'client_changed_his_mind' },
          { text: this.$t('subOrder.deterioratedProducts'), value: 'deteriorated_products' },
          { text: this.$t('subOrder.orderedMultipleTimes'), value: 'ordered_multiple_times' },
          { text: this.$t('subOrder.productsNotAvailable'), value: 'products_not_available' },
          { text: this.$t('subOrder.customerDidNotCollect'), value: 'customer_did_not_collect_order' },
        ]
        const itemCancelReasonsTranslations = [
          { text: this.$t('subOrder.notAvailable'), value: 'not_available' },
          { text: this.$t('subOrder.defective'), value: 'defective' },
          { text: this.$t('subOrder.alreadyReserved'), value: 'already_reserved' },
        ]
        if (settings.display) {
          if (settings.display.order_cancel_reasons) {
            this.cancelReasons = []
            for (const orderCancelReason of settings.display.order_cancel_reasons) {
              const translatedCancelReason = cancelReasonsTranslations.find((translatedCancelReason) => {
                return orderCancelReason === translatedCancelReason.value
              })
              if (translatedCancelReason) {
                this.cancelReasons.push(translatedCancelReason)
              } else {
                this.cancelReasons.push({ text: orderCancelReason, value: orderCancelReason })
              }
            }
          } else {
            this.cancelReasons = cancelReasonsTranslations
          }
          if (settings.display.display_custom_cancel_reason) {
            this.cancelReasons.push({ text: this.$t('subOrder.otherReason'), value: 'other' })
          }

          if (settings.display.item_cancel_reasons) {
            this.itemCancelReasons = []
            for (const itemCancelReason of settings.display.item_cancel_reasons) {
              const translatedCancelReason = itemCancelReasonsTranslations.find((translatedCancelReason) => {
                return itemCancelReason === translatedCancelReason.value
              })
              if (translatedCancelReason) {
                this.itemCancelReasons.push(translatedCancelReason)
              } else {
                this.itemCancelReasons.push({ text: itemCancelReason, value: itemCancelReason })
              }
            }
          } else {
            this.itemCancelReasons = itemCancelReasonsTranslations
          }
        } else {
          this.cancelReasons = cancelReasonsTranslations
          this.itemCancelReasons = itemCancelReasonsTranslations
        }
      },
      validateCustomerForm(values) {
        this.$set(this.subOrder, 'customer', values.data)
        let patchData = {
          customer: this.subOrder.customer,
        }
        this.patchSubOrder(patchData, true)
      },
      async copyToClipboard(text) {
        try {
          await navigator.clipboard.writeText(text)
          displayMessage(this.$t('subOrder.copySuccessful'), 'success')
        } catch (e) {
          displayMessage(this.$t('subOrder.copyFailed'), 'error')
        }
      },
      getSubOrderPickPackShip() {
        this.loadingPickPackShip = true
        EventBus.$emit('displayGlobalOverlay', {
          display: true,
          message: this.$t('subOrder.getPickPackShipsInProgress'),
        })
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            page: 1,
            itemsPerPage: 1,
            location_id: this.isCustomerService ? undefined : [this.currentStore],
            status: ['picking', 'packing'],
            sub_orders_id: [this.subOrder.order_id],
          },
        }
        this.$Amplify.API.get('pick-pack-ships', '/v1/int/pick-pack-ships', myInit)
          .then((res) => {
            if (res.data && res.data.length) {
              this.subOrderPickPackShip = { reference: res.data[0]._id, picker: res.data[0].picker }
            }
          })
          .catch((e) => {
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('subOrder.errorLoadingPickPackShips'),
              color: 'error',
            })
          })
          .finally(() => {
            this.loadingPickPackShip = false
          })
        EventBus.$emit('displayGlobalOverlay', { display: false })
      },
    },
  }
</script>
<style scoped>
  .v-btn-footer {
    max-width: 97%;
  }
  button >>> .v-btn__content {
    width: 100%;
    white-space: normal;
  }

  .no-transform {
    text-transform: none !important;
  }

  .btn-nowrap >>> .v-btn__content {
    white-space: nowrap;
    width: auto;
  }

  @media (min-width: 1340px) {
    /* Align stepper with product content on desktop */
    /* eslint-disable-next-line */
    .preparation-stepper >>> .v-stepper__header {
      margin-left: 105px;
      margin-right: 400px;
      box-shadow: none !important;
    }
  }

  /* Vuetify 2 hides .v-stepper__label on sm-and-down via media query even with alt-labels */
  /* eslint-disable-next-line */
  .preparation-stepper >>> .v-stepper__label {
    display: block !important;
    text-align: center;
  }

  /* eslint-disable-next-line */
  .preparation-stepper >>> .v-stepper__step:hover {
    background: transparent !important;
  }

  .theme--dark.v-stepper {
    background: transparent;
  }

  .v-btn-footer {
    max-width: 97%;
  }

  button >>> .v-btn__content {
    width: 100%;
    white-space: normal;
  }
  .error-text-mobile {
    font-size: 0.75rem;
    line-height: normal;
  }
</style>
